import { useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { PreJoinScreen } from './PreJoinScreen';
import { VideoCallRoom } from './VideoCallRoom';
import { WaitingRoom } from './WaitingRoom';
import { useTranslation } from '../../hooks/useTranslation';
import type { MeetingContext } from '../../stores/videoCallStore';
import { getApiEndpoints } from '@business-assistant/shared/config';
import './VideoCall.css';

export function GuestMeetingPage() {
  const { t } = useTranslation();
  const { roomSlug } = useParams<{ roomSlug: string }>();
  const [token, setToken] = useState<string | null>(null);
  const [wsUrl, setWsUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [guestName, setGuestName] = useState('');
  const [meetingContext, setMeetingContext] = useState<MeetingContext | null>(null);
  const [showWaitingRoom, setShowWaitingRoom] = useState(false);

  const handleJoin = useCallback(async (name: string) => {
    if (!roomSlug) return;
    setIsConnecting(true);
    setError(null);
    setGuestName(name);

    try {
      const response = await fetch(`${getApiEndpoints().livekit}/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roomSlug, guestName: name }),
      });

      const data = await response.json();
      if (!data.success) {
        setError(data.error || t('videocall.joinError'));
        return;
      }

      // Check meeting context from token response
      if (data.meeting) {
        setMeetingContext(data.meeting);

        if (data.meeting.status === 'active') {
          // Meeting is active, connect directly
          setToken(data.token);
          setWsUrl(data.wsUrl);
        } else {
          // Meeting not active yet, show waiting room
          // Store token for later use when meeting starts
          setToken(data.token);
          setWsUrl(data.wsUrl);
          setShowWaitingRoom(true);
        }
      } else {
        // No meeting context (legacy room without metadata), connect directly
        setToken(data.token);
        setWsUrl(data.wsUrl);
      }
    } catch {
      setError(t('videocall.connectionError'));
    } finally {
      setIsConnecting(false);
    }
  }, [roomSlug, t]);

  const handleMeetingActive = useCallback(() => {
    // Meeting became active, exit waiting room and connect
    setShowWaitingRoom(false);
  }, []);

  const handleLeave = useCallback(() => {
    setToken(null);
    setWsUrl(null);
    setShowWaitingRoom(false);
    setMeetingContext(null);
    setGuestName('');
  }, []);

  if (!roomSlug) {
    return (
      <div className="videocall-guest">
        <h1 className="videocall-guest__title">{t('videocall.invalidLink')}</h1>
      </div>
    );
  }

  // Show waiting room if meeting not active
  if (showWaitingRoom && meetingContext && meetingContext.status !== 'active') {
    return (
      <div className="videocall-guest">
        <WaitingRoom
          meetingContext={meetingContext}
          roomSlug={roomSlug}
          guestName={guestName}
          onMeetingActive={handleMeetingActive}
          isOrganizer={meetingContext.isOrganizer}
        />
      </div>
    );
  }

  // Show video room if connected
  if (token && wsUrl && !showWaitingRoom) {
    return <VideoCallRoom token={token} serverUrl={wsUrl} onLeave={handleLeave} isGuest />;
  }

  // Show pre-join screen
  return (
    <div className="videocall-guest">
      <h1 className="videocall-guest__title">{t('videocall.guestWelcome')}</h1>
      {meetingContext?.title && (
        <p style={{ color: '#7C3AED', fontSize: '1.125rem', fontWeight: 500, margin: '0 0 1rem' }}>
          {meetingContext.title}
        </p>
      )}
      {error && <p style={{ color: '#ef4444' }}>{error}</p>}
      <div className="videocall-guest__form">
        <PreJoinScreen onJoin={handleJoin} isConnecting={isConnecting} />
      </div>
    </div>
  );
}
